import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { X, Cookie, Shield } from 'lucide-react';

const STORAGE_KEY = 'cookie-consent';

type ConsentState = 'pending' | 'accepted' | 'declined';

export default function PrivacyBanner() {
  const [consent, setConsent] = useState<ConsentState>('pending');
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY) as ConsentState | null;
    if (saved === 'accepted' || saved === 'declined') {
      setConsent(saved);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem(STORAGE_KEY, 'accepted');
    setConsent('accepted');
    // Enable GTM/GA4 after consent
    const w = window as unknown as Record<string, unknown>;
    if (w.dataLayer && Array.isArray(w.dataLayer)) {
      (w.dataLayer as unknown[]).push({ event: 'cookie_consent_accepted' });
    }
  };

  const handleDecline = () => {
    localStorage.setItem(STORAGE_KEY, 'declined');
    setConsent('declined');
  };

  if (consent !== 'pending') return null;

  return (
    <>
      {/* Main Banner */}
      <div
        role="dialog"
        aria-label="Cookie consent"
        className="fixed bottom-0 left-0 right-0 z-50 bg-[#0057B7] border-t border-white/10 shadow-2xl"
      >
        <div className="container-custom py-4 md:py-5">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
            <Cookie className="w-5 h-5 text-[#FFDD00] shrink-0 mt-0.5 md:mt-0" />
            <div className="flex-1">
              <p className="text-sm text-white/80 leading-relaxed">
                We use cookies and similar technologies to enhance your browsing experience,
                analyse site traffic, and personalise content. By clicking &ldquo;Accept All&rdquo;,
                you consent to our use of cookies as described in our{' '}
                <Link to="/privacy" className="text-[#FFDD00] hover:underline font-medium">Privacy Policy</Link>.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2 shrink-0">
              <button
                onClick={() => setShowDetails(!showDetails)}
                className="px-4 py-2 text-sm text-white/60 hover:text-white border border-white/10 rounded-lg hover:border-white/20 transition-colors"
              >
                {showDetails ? 'Hide' : 'Manage'}
              </button>
              <button
                onClick={handleDecline}
                className="px-4 py-2 text-sm text-white/60 hover:text-white border border-white/10 rounded-lg hover:border-white/20 transition-colors"
              >
                Decline
              </button>
              <button
                onClick={handleAccept}
                className="px-5 py-2 text-sm font-semibold bg-[#FFDD00] text-[#0057B7] rounded-lg hover:bg-[#C9A800] transition-colors"
              >
                Accept All
              </button>
              <button
                onClick={handleDecline}
                className="p-2 text-white/30 hover:text-white/60 transition-colors md:hidden"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Detailed Preferences */}
        {showDetails && (
          <div className="border-t border-white/5 bg-[#003D80]">
            <div className="container-custom py-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="flex items-start gap-3 bg-white/5 rounded-lg p-4">
                  <Shield className="w-4 h-4 text-[#FFDD00] mt-0.5 shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-white">Essential</p>
                    <p className="text-xs text-white/50 mt-1">Required for the site to function. Cannot be disabled.</p>
                    <span className="inline-block mt-2 px-2 py-0.5 bg-[#FFDD00]/20 text-[#0057B7] text-[10px] font-semibold rounded">Always On</span>
                  </div>
                </div>
                <div className="flex items-start gap-3 bg-white/5 rounded-lg p-4">
                  <Shield className="w-4 h-4 text-white/40 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-white">Analytics</p>
                    <p className="text-xs text-white/50 mt-1">Google Analytics 4 helps us understand how visitors use our site.</p>
                    <span className="inline-block mt-2 px-2 py-0.5 bg-white/10 text-white/40 text-[10px] font-semibold rounded">Requires consent</span>
                  </div>
                </div>
                <div className="flex items-start gap-3 bg-white/5 rounded-lg p-4">
                  <Shield className="w-4 h-4 text-white/40 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-white">Marketing</p>
                    <p className="text-xs text-white/50 mt-1">Google Ads conversion tracking and remarketing pixels.</p>
                    <span className="inline-block mt-2 px-2 py-0.5 bg-white/10 text-white/40 text-[10px] font-semibold rounded">Requires consent</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
